import React, { useRef, useState, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import InputWithChecks from "../../shared/InputWithChecks";
import Container from "../../shared/Container";
import PrimaryButton from "../../shared/PrimaryButton";
import BoxBorder from "../../shared/BoxBorder";
import LoadingIndicatorButton from "../../shared/LoadingIndicatorButton";
import {
  validateRequired,
  validateForm,
  validateMinLength,
  validatePositiveNumber,
} from "../../../util/shared/validation";
import { addMenu, updateMenu, fetchProducts } from "../../../util/admin/httpAdmin";
import plusIconWhite from "../../../assets/admin/icons/plus-white.svg";
import SuccessMessage from "../../shared/SuccessMessage";
import { queryClient } from "../../../util/admin/httpAdmin";
import CollectionDropdown from "../products/CollectionDropdown";
import ProductOptionsEditor from "../shared/ProductOptionsEditor";

import placeholderImage from "../../../assets/admin/images/placeholder.svg"; // Lokale placeholder afbeelding

const MenuFormAdmin = ({ onSuccessClose, editMode = false, previousData = {} }) => {
  const nameRef = useRef(null);
  const descriptionRef = useRef(null);
  const priceRef = useRef(null);
  const productsRef = useRef(null);
  const collectionRef = useRef(null);
  const navigate = useNavigate();

  // Add refs for product options
  const optionNameRefs = useRef([]);
  const choiceNameRefs = useRef([]);
  const choicePriceRefs = useRef([]);
  const maxSelectionsRefs = useRef([]);

  const [successMessage, setSuccessMessage] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(
    editMode && previousData.hasImage
      ? `https://resto-online.s3.eu-north-1.amazonaws.com/${previousData._id}`
      : placeholderImage
  );

  // Add state for product options
  const [productOptions, setProductOptions] = useState(
    editMode && previousData.productOptions 
      ? previousData.productOptions.map(option => ({
          ...option,
          options: option.options.map(choice => ({
            ...choice,
            price: choice.price?.$numberDecimal || choice.price || 0
          })),
          maxSelections: option.maxSelections || 1
        }))
      : []
  );

  // Add effect to initialize refs arrays
  useEffect(() => {
    // Initialize refs arrays
    optionNameRefs.current = optionNameRefs.current.slice(0, productOptions.length);
    choiceNameRefs.current = choiceNameRefs.current.slice(0, productOptions.length);
    choicePriceRefs.current = choicePriceRefs.current.slice(0, productOptions.length);
    maxSelectionsRefs.current = maxSelectionsRefs.current.slice(0, productOptions.length);
  }, [productOptions]);

  const { data: products, isLoading: productsLoading } = useQuery({
    queryKey: ["adminProducts"],
    queryFn: fetchProducts,
  });

  const closeTimeoutRef = useRef(null);
  const messageTimeoutRef = useRef(null);

  useEffect(() => {
    // Cleanup function
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
      if (messageTimeoutRef.current) {
        clearTimeout(messageTimeoutRef.current);
      }
    };
  }, []);

  const mutationFn = editMode ? updateMenu : addMenu;

  const { mutate, isPending, isError, error } = useMutation({
    mutationFn,
    onSuccess: () => {
      setSuccessMessage(
        editMode ? "Menu succesvol bijgewerkt!" : "Menu succesvol toegevoegd!"
      );
      queryClient.invalidateQueries(["adminMenus"]);
      
      closeTimeoutRef.current = setTimeout(() => {
        if (onSuccessClose) {
          onSuccessClose();
        }
        messageTimeoutRef.current = setTimeout(() => {
          setSuccessMessage("");
        }, 100);
      }, 500);
    },
    onError: (error) => {
      console.error("Error in menu mutation:", error);
      console.error("Error details:", {
        message: error.message,
        status: error.status,
        info: error.info,
        stack: error.stack
      });
    }
  });

  const productsOptions = React.useMemo(
    () =>
      products?.map((product) => ({
        id: product._id,
        label: product.name,
      })),
    [products]
  );

  const selectedProductsValue = React.useMemo(
    () => (editMode && previousData.products ? previousData.products.map((p) => p._id) : []),
    [editMode, previousData.products]
  );

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file)); // Toon een preview
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();

    const nameValid = nameRef.current?.validateInput();
    const priceValid = priceRef.current?.validateInput();

    if (nameValid && priceValid) {
      const formData = new FormData();
      
      // Basis menu data
      formData.append("name", nameRef.current.getValue());
      formData.append("description", descriptionRef.current.getValue());
      formData.append("price", parseFloat(priceRef.current.getValue()));
      formData.append("collection", collectionRef.current.getValue());

      // Verwerk producten
      const selectedProducts = productsRef.current.getValue();
      const cleanedProducts = selectedProducts
        ? (Array.isArray(selectedProducts) ? selectedProducts : selectedProducts.split(','))
          .map(id => id.trim())
          .filter(id => id) // Filter lege waarden
        : [];
      
      // Voeg elk product ID apart toe aan de FormData
      cleanedProducts.forEach((productId, index) => {
        formData.append(`products[${index}]`, productId);
      });

      // Voeg image toe indien aanwezig
      if (imageFile) {
        formData.append("image", imageFile);
      }

      // Voeg ID toe bij bewerken
      if (editMode && previousData?._id) {
        formData.append("_id", previousData._id);
      }

      // Update product options met actuele waarden uit refs
      const updatedOptions = productOptions.map((option, optionIndex) => {
        const updatedOption = { ...option };
        try {
          // Als refs aanwezig, gebruik die waarden
          if (optionNameRefs.current[optionIndex] && typeof optionNameRefs.current[optionIndex].getValue === 'function') {
            updatedOption.name = optionNameRefs.current[optionIndex].getValue() || "";
          }
          
          if (option.type === "select" || option.type === "multiselect") {
            updatedOption.options = option.options.map((choice, choiceIndex) => {
              // Maak een gekopieerde keuze
              const updatedChoice = { ...choice };
              
              // Update naam als ref bestaat
              if (choiceNameRefs.current[optionIndex] && 
                choiceNameRefs.current[optionIndex][choiceIndex] && 
                typeof choiceNameRefs.current[optionIndex][choiceIndex].getValue === 'function') {
                  updatedChoice.name = choiceNameRefs.current[optionIndex][choiceIndex].getValue() || "";
              }
              
              // Update prijs als ref bestaat
              if (choicePriceRefs.current[optionIndex] && 
                choicePriceRefs.current[optionIndex][choiceIndex] && 
                typeof choicePriceRefs.current[optionIndex][choiceIndex].getValue === 'function') {
                  updatedChoice.price = parseFloat(choicePriceRefs.current[optionIndex][choiceIndex].getValue() || 0);
              }
              
              return updatedChoice;
            });
            
            // Als het multiselect is, zet maxSelections expliciet
            if (option.type === "multiselect" && 
              maxSelectionsRefs.current[optionIndex] && 
              typeof maxSelectionsRefs.current[optionIndex].getValue === 'function') {
              const maxSelectionsValue = maxSelectionsRefs.current[optionIndex].getValue();
              updatedOption.maxSelections = maxSelectionsValue ? parseInt(maxSelectionsValue) : option.maxSelections || 1;
            }
          }
        } catch (error) {
          console.error(`Error bij bijwerken van optie ${optionIndex}:`, error);
        }
        
        return updatedOption;
      });

      // Converteer product opties naar JSON en voeg toe aan form data
      formData.append("productOptions", JSON.stringify(updatedOptions));

      // Log de formdata voor debugging
      for (let [key, value] of formData.entries()) {
        console.log(key, value);
      }

      mutate(formData);
    }
  };

  // Add function to handle adding new product option
  const handleAddProductOption = () => {
    setProductOptions(prev => [...prev, {
      name: "",
      type: "select",
      options: [],
      maxSelections: 1
    }]);
  };

  // Add function to handle removing product option
  const handleRemoveProductOption = (index) => {
    setProductOptions(prev => prev.filter((_, i) => i !== index));
  };

  // Add function to handle updating product option
  const handleUpdateProductOption = (index, updatedOption) => {
    setProductOptions(prev => prev.map((option, i) => 
      i === index ? updatedOption : option
    ));
  };

  // Add function to handle adding new option choice
  const handleAddOptionChoice = (optionIndex) => {
    setProductOptions(prev => prev.map((option, i) => 
      i === optionIndex 
        ? {
            ...option,
            options: [...option.options, { name: "", price: 0 }]
          }
        : option
    ));
  };

  // Add function to handle removing option choice
  const handleRemoveOptionChoice = (optionIndex, choiceIndex) => {
    setProductOptions(prev => prev.map((option, i) => 
      i === optionIndex 
        ? {
            ...option,
            options: option.options.filter((_, j) => j !== choiceIndex)
          }
        : option
    ));
  };

  // Add function to handle updating option choice
  const handleUpdateOptionChoice = (optionIndex, choiceIndex, updatedChoice) => {
    setProductOptions(prev => prev.map((option, i) => 
      i === optionIndex 
        ? {
            ...option,
            options: option.options.map((choice, j) => 
              j === choiceIndex ? updatedChoice : choice
            )
          }
        : option
    ));
  };

  return (
    <section>
      <Container>
        {successMessage ? (
          <SuccessMessage title="Succes!" description={successMessage} />
        ) : (
          <BoxBorder extraClasses="mxw-1000 m-a g-20">
            <h2>{editMode ? "Menu Bewerken" : "Nieuw Menu Toevoegen"}</h2>
            {productsLoading ? (
              <p>Laden...</p>
            ) : products?.length === 0 ? (
              <div className="g-10">
                <p className="c-r">
                  Er zijn nog geen producten beschikbaar. Maak eerst producten aan
                  voordat je een menu kunt maken.
                </p>
                <div>
                  <PrimaryButton onClick={() => navigate("/dashboard/producten?add=true")}>
                    <div className="g-15-1fr f-s">
                      <img src={plusIconWhite} alt="add" />
                      <span className="flex-0-5">Voeg producten eerst toe</span>
                    </div>
                  </PrimaryButton>
                </div>
              </div>
            ) : (
              <form className="g-20" onSubmit={handleFormSubmit}>
                <div className="g-1-1 a-i-s gap-1">
                  <InputWithChecks
                    ref={nameRef}
                    label="Menunaam"
                    inputType="text"
                    validate={(value) =>
                      validateForm(value, [
                        validateRequired,
                        (v) => validateMinLength(v, 2),
                      ])
                    }
                    placeholder="Voer de menunaam in"
                    value={editMode ? previousData.name.toString() : ""}
                  />
                  <InputWithChecks
                    ref={descriptionRef}
                    label="Beschrijving"
                    inputType="textarea"
                    placeholder="Voer een menuomschrijving in"
                    value={editMode ? previousData.description : ""}
                  />
                  <InputWithChecks
                    ref={priceRef}
                    label="Prijs"
                    inputType="number"
                    validate={(value) =>
                      validateForm(value, [validateRequired, validatePositiveNumber])
                    }
                    placeholder="Voer de menuprijs in"
                    value={editMode && previousData.price ? 
                      (previousData.price.$numberDecimal || previousData.price).toString() 
                      : ""}
                  />
                  <InputWithChecks
                    ref={productsRef}
                    label="Selecteer Producten"
                    inputType="multipleSearchDropdown"
                    options={productsOptions}
                    value={selectedProductsValue}
                    validate={validateRequired}
                    placeholder="Kies producten"
                    info="Selecteer meerdere producten door checkboxen aan te vinken."
                  />

                  <CollectionDropdown ref={collectionRef} value={editMode && previousData.collection ? previousData.collection._id : ""} />

                  {/* Afbeelding upload veld */}
                  <div className="g-10">
                    <label>Menu Afbeelding</label>
                    <input type="file" accept="image/*" onChange={handleImageChange} />
                    <img src={imagePreview} alt="Menu preview" className="mxw-150" />
                  </div>
                </div>

                {/* Product Options Editor */}
                <div className="g-10">
                  <h3>Product Opties</h3>
                  <ProductOptionsEditor 
                    productOptions={productOptions} 
                    setProductOptions={setProductOptions} 
                    refs={{
                      optionNameRefs,
                      choiceNameRefs,
                      choicePriceRefs,
                      maxSelectionsRefs
                    }}
                    onAddOption={handleAddProductOption}
                    onRemoveOption={handleRemoveProductOption}
                    onUpdateOption={handleUpdateProductOption}
                    onAddChoice={handleAddOptionChoice}
                    onRemoveChoice={handleRemoveOptionChoice}
                    onUpdateChoice={handleUpdateOptionChoice}
                  />
                </div>

                {isError && <div className="c-r">{error.info?.message || "Er is iets fout gegaan tijdens het verwerken van het menu."}</div>}
                
                <div>
                  <PrimaryButton type="submit" disabled={isPending}>
                    <div className="g-15-1fr">
                      <img src={plusIconWhite} alt="add" />
                      <span className="flex-0-5">{editMode ? "Menu Bijwerken" : "Menu Toevoegen"} {isPending && <LoadingIndicatorButton />}</span>
                    </div>
                  </PrimaryButton>
                </div>
              </form>
            )}
          </BoxBorder>
        )}
      </Container>
    </section>
  );
};

export default MenuFormAdmin;
